import { Link } from 'react-router-dom'
import { Home, Shield, X } from 'lucide-react'
import { useDirectorStateV2 } from '../hooks/useDirectorStateV2'
import { CardGrid } from '../components/director_v2/CardGrid'
import { DirectorConsole } from '../components/director_v2/DirectorConsole'
import { StoryboardTimeline } from '../components/director_v2/StoryboardTimeline'
import { STYLE_CATEGORIES } from '../components/director_v2/styleOptions'

export function DirectorPageV2() {
  const state = useDirectorStateV2()

  return (
    <div className="min-h-screen bg-gray-900 text-gray-100">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold">🎬 导演工作台 V2</h1>
          <div className="flex gap-2">
            <Link
              to="/"
              className="flex items-center px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm"
            >
              <Home className="w-3.5 h-3.5 mr-1.5" />
              回到首页
            </Link>
            <Link
              to="/admin"
              className="flex items-center px-3 py-1.5 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-sm"
            >
              <Shield className="w-3.5 h-3.5 mr-1.5" />
              管理界面
            </Link>
          </div>
        </div>

        {/* 第一步 + 第二步：左右布局（1:1） */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
          {/* ModuleA: 灵感起爆区（左侧 1/2） */}
          <div className="bg-gray-800 rounded-lg p-3 border border-gray-700 lg:col-span-1">
            <div className="flex justify-between items-center mb-2">
              <h2 className="text-base font-semibold">✨ 第一步：描述你的画面灵感</h2>
              <button
                onClick={state.handleNewCreation}
                className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-xs font-medium"
              >
                重新开始
              </button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-[1fr,auto,auto] gap-2 items-end">
                <div>
                  <label className="block text-xs font-medium mb-1">画面灵感 *</label>
                  <textarea
                    value={state.seedPrompt}
                    onChange={(e) => state.setSeedPrompt(e.target.value)}
                    placeholder="用几个词描述你想要的画面，例如：星球大战、雨中的东京街头、水墨风山水"
                    className="w-full px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                    rows={2}
                    disabled={state.isGeneratingT2I}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium mb-1">生成数量</label>
                  <select
                    value={state.imageCount}
                    onChange={(e) => state.setImageCount(Number(e.target.value))}
                    className="px-2 py-1.5 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-white text-sm"
                    disabled={state.isGeneratingT2I}
                  >
                    <option value={1}>1</option>
                    <option value={3}>3</option>
                    <option value={5}>5</option>
                    <option value={10}>10</option>
                  </select>
                </div>
                <button
                  onClick={state.handleGenerateT2I}
                  disabled={state.isGeneratingT2I || !state.seedPrompt.trim()}
                  className="px-4 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed rounded-lg font-medium transition-colors whitespace-nowrap text-sm"
                >
                  {state.isGeneratingT2I ? '⏳ 生成中...' : '✨ 生成画面方案'}
                </button>
              </div>

              {/* 风格下拉框列表 */}
              <div className="space-y-1.5 pt-1">
                {STYLE_CATEGORIES.map((cat) => {
                  const isActive = !!state.styleSelections[cat.key]
                  return (
                    <div key={cat.key} className="grid grid-cols-[180px,1fr] gap-2 items-center">
                      <label className={`text-xs font-medium ${isActive ? 'text-purple-300 font-semibold' : 'text-gray-300'}`}>{cat.label}</label>
                      <select
                        value={state.styleSelections[cat.key] || ''}
                        onChange={(e) => state.setStyleSelection(cat.key, e.target.value)}
                        disabled={state.isGeneratingT2I}
                        className={`w-full px-2 py-1.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:opacity-50 ${
                          isActive
                            ? 'bg-purple-900/40 border-purple-500 text-purple-100'
                            : 'bg-gray-700 border-gray-600 text-white'
                        }`}
                      >
                        <option value="" className="bg-gray-700 text-white">不指定</option>
                        {cat.options.map((opt) => (
                          <option key={opt.value} value={opt.value} className="bg-gray-700 text-white">{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>

          {/* ModuleB: 十连抽选卡区（右侧 1/2） */}
          <div className="lg:col-span-1">
            {state.cards.length > 0 ? (
              <CardGrid
                cards={state.cards}
                selectedCardId={state.selectedCardId}
                onSelectCard={state.setSelectedCardId}
                onGeneratePreview={state.handleGeneratePreview}
                previewImages={state.previewImages}
                batchPreviewMode={state.batchPreviewMode}
                batchGenerating={state.batchGenerating}
                isGeneratingT2I={state.isGeneratingT2I}
                onBatchPreview={state.handleBatchPreview}
                onOpenPreviewModal={state.handleOpenPreviewModal}
                variantTargetCardId={state.variantTargetCardId}
                setVariantTargetCardId={state.setVariantTargetCardId}
                variantModification={state.variantModification}
                setVariantModification={state.setVariantModification}
                isGeneratingVariant={state.isGeneratingVariant}
                onGenerateVariant={state.handleGenerateVariant}
              />
            ) : (
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-700 h-full flex items-center justify-center min-h-[300px]">
                <p className="text-sm text-gray-500">生成画面方案后将在此显示</p>
              </div>
            )}
          </div>
        </div>

        {/* ModuleC: 导演控制台 */}
        {state.selectedCard && (
          <DirectorConsole
            selectedCard={state.selectedCard}
            videoRequirement={state.videoRequirement}
            setVideoRequirement={state.setVideoRequirement}
            durationMode={state.durationMode}
            setDurationMode={state.setDurationMode}
            isGeneratingI2V={state.isGeneratingI2V}
            onGenerateI2V={state.handleGenerateI2V}
            cameraMovements={state.cameraMovements}
            setCameraMovements={state.setCameraMovements}
            rhythm={state.rhythm}
            setRhythm={state.setRhythm}
          />
        )}

        {/* ModuleD: 分镜剧本时间轴 */}
        {state.currentI2vResult && (
          <StoryboardTimeline
            i2vResult={state.currentI2vResult}
            videoRefineInput={state.videoRefineInput}
            setVideoRefineInput={state.setVideoRefineInput}
            isRefiningVideo={state.isRefiningVideo}
            onRefineVideoPrompt={state.handleRefineVideoPrompt}
          />
        )}
      </div>

      {/* 预览图片 Modal */}
      {state.previewModalImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={() => state.setPreviewModalImage(null)}
        >
          <button
            onClick={() => state.setPreviewModalImage(null)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>
          <div className="relative max-w-5xl max-h-[90vh] flex flex-col">
            <img
              src={state.previewModalImage.url}
              alt={state.previewModalImage.prompt}
              className="max-w-full max-h-[80vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="mt-4 p-4 bg-gray-800/90 rounded-lg max-w-3xl mx-auto">
              <p className="text-sm text-gray-300">{state.previewModalImage.prompt}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
